/**
 * conflictService — Pure-JS module for conflict storage and device identity.
 *
 * No React dependencies — can be imported from hooks, services, or components.
 */

const CONFLICTS_KEY  = 'adhd-planner-conflicts';
const DEVICE_ID_KEY  = 'adhd-planner-device-id';

// ─── Device identity ──────────────────────────────────────────────────────────

/**
 * Return a stable device identifier, creating one on first call.
 */
export function getDeviceId() {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = `dev-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
}

// ─── Conflict store ───────────────────────────────────────────────────────────

/** @typedef {{ entityId: string, entityType: string, local: object, remote: object, detectedAt: string }} ConflictEntry */

/** @returns {{ [entityId: string]: ConflictEntry }} */
export function getConflicts() {
  try {
    const raw = localStorage.getItem(CONFLICTS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function _saveConflicts(conflicts) {
  localStorage.setItem(CONFLICTS_KEY, JSON.stringify(conflicts));
  _listeners.forEach(fn => fn(conflicts));
}

/**
 * Record a conflict between a local and remote version.
 * @param {string} entityId
 * @param {object} local
 * @param {object} remote
 * @param {'task'|'event'} entityType
 */
export function addConflict(entityId, local, remote, entityType = 'task') {
  const conflicts = getConflicts();
  conflicts[entityId] = {
    entityId,
    entityType,
    local,
    remote,
    detectedAt: new Date().toISOString(),
  };
  _saveConflicts(conflicts);
}

/**
 * Remove a resolved conflict.
 */
export function resolveConflict(entityId) {
  const conflicts = getConflicts();
  delete conflicts[entityId];
  _saveConflicts(conflicts);
}

export function clearAllConflicts() {
  localStorage.removeItem(CONFLICTS_KEY);
  _listeners.forEach(fn => fn({}));
}

export function getConflictCount() {
  return Object.keys(getConflicts()).length;
}

// ─── Listeners ────────────────────────────────────────────────────────────────

const _listeners = new Set();

/** Subscribe to conflict-store changes. Returns an unsubscribe function. */
export function subscribeToConflicts(fn) {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
}

// ─── Intelligent merge strategies ────────────────────────────────────────────

/**
 * Auto-resolve a conflict using intelligent merge.
 * Strategy:
 *   1. If one side is empty/null → take the other
 *   2. If timestamps differ → take the most recent
 *   3. If both changed different fields → merge fields
 *   4. If same field changed → take the most recent value
 *
 * @param {ConflictEntry} conflict
 * @returns {{ resolved: object, strategy: string }}
 */
export function autoResolve(conflict) {
  const { local, remote } = conflict;

  // Case 1: one side missing
  if (!local || Object.keys(local).length === 0) {
    return { resolved: remote, strategy: 'remote_only' };
  }
  if (!remote || Object.keys(remote).length === 0) {
    return { resolved: local, strategy: 'local_only' };
  }

  // Get timestamps
  const localTime = new Date(local.updatedAt || local.createdAt || 0).getTime();
  const remoteTime = new Date(remote.updatedAt || remote.createdAt || 0).getTime();

  // Case 2: identical content
  const localStr = JSON.stringify(local, Object.keys(local).sort());
  const remoteStr = JSON.stringify(remote, Object.keys(remote).sort());
  if (localStr === remoteStr) {
    return { resolved: local, strategy: 'identical' };
  }

  // Case 3: field-level merge
  const merged = { ...local };
  const changedFields = [];

  for (const key of Object.keys(remote)) {
    if (key === 'updatedAt' || key === 'createdAt' || key === 'version' || key === 'deviceId') continue;

    const localVal = JSON.stringify(local[key]);
    const remoteVal = JSON.stringify(remote[key]);

    if (localVal !== remoteVal) {
      // Both changed this field → take most recent
      if (remoteTime > localTime) {
        merged[key] = remote[key];
        changedFields.push(key);
      }
      // else keep local (already in merged)
    }
  }

  // Add any remote-only keys
  for (const key of Object.keys(remote)) {
    if (!(key in local)) {
      merged[key] = remote[key];
    }
  }

  // Update metadata
  merged.updatedAt = new Date().toISOString();
  merged.version = Math.max(local.version || 0, remote.version || 0) + 1;
  merged.deviceId = getDeviceId();

  const strategy = changedFields.length > 0
    ? `merged_${changedFields.length}_fields`
    : 'local_wins';

  return { resolved: merged, strategy };
}

/**
 * Auto-resolve all pending conflicts.
 * @returns {{ resolved: number, strategies: string[] }}
 */
export function autoResolveAll() {
  const conflicts = getConflicts();
  const entries = Object.values(conflicts);
  const strategies = [];

  for (const conflict of entries) {
    const { resolved, strategy } = autoResolve(conflict);
    strategies.push(`${conflict.entityId}: ${strategy}`);
    resolveConflict(conflict.entityId);
    // The resolved object should be persisted by the caller
    // We emit it via a callback
    _listeners.forEach(fn => fn(getConflicts(), { type: 'auto_resolved', entityId: conflict.entityId, resolved }));
  }

  return { resolved: entries.length, strategies };
}
